import axios from 'axios';
import type { TeamOld } from '@getaccept/lib-shared/src/users/types/team-old';
import { GraphQLFetchPolicy } from '@getaccept/lib-shared/src/graphql/graphql-fetch-policy';
import bugsnagClient from '@getaccept/lib-shared/src/bugsnag';
import { apolloClientInternal } from '@getaccept/lib-shared/src/internal/apollo-client-internal';
import { UserQueries } from '../graphql/user.queries';
import type { User as CurrentUser, User } from '../../../user/types/user';
import type { Response } from '../types/response';
import type { ExternalIdentityProvider } from '../types/external-identity-provider';
import type { UpdateUserArgsV2 } from '../types/update-user-args-v2';
import { UserMutations } from '../graphql/user.mutations';
import type { UpdateUserProfileInput } from '../types/update-user-profile-input';
import type { CreateUserProfileArgs } from '../types/create-user-profile-args';

const saveSettingsUrl = '/save/settings';
export class UserService {
  private static readonly registrationSourceFromSettings = 'settings';

  static async load(): Promise<User[]> {
    const {
      data: { users },
    } = await apolloClientInternal.query({
      query: UserQueries.load,
    });
    return users;
  }

  static async loadCurrentUser(): Promise<CurrentUser> {
    const {
      data: { me },
    } = await apolloClientInternal.query({
      query: UserQueries.me,
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });

    return {
      firstName: me.firstName,
      lastName: me.lastName,
      fullName: `${me.firstName} ${me.lastName}`,
      id: me.id,
      email: me.email,
      thumbUrl: me.thumbUrl,
      title: me.title,
      mobile: me.mobile,
      phone: me.phone,
      nbrDocumentsSent: me.nbrDocumentsSent,
      registrationCampaign: me.registrationCampaign,
      team: me.team,
      timezone: me.timezone,
      language: me.language,
      userProfile: me.userProfile,
      bio: me.bio,
      meetingLink: me.meetingLink,
      imageScale: me.imageScale,
      imageTranslateX: me.imageTranslateX,
      imageTranslateY: me.imageTranslateY,
    };
  }

  static async loadOne(userId: string): Promise<User> {
    const {
      data: { user },
    } = await apolloClientInternal.query({
      query: UserQueries.loadUser,
      variables: {
        userId,
      },
    });
    return user;
  }

  static async loadTeam(): Promise<TeamOld> {
    const {
      data: { me },
    } = await apolloClientInternal.query({
      query: UserQueries.getTeam,
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return me.team;
  }

  static async changePassword(
    userId: string,
    oldPassword: string,
    newPassword: string
  ): Promise<Response> {
    const body = new FormData();
    body.append('action', 'password');
    body.append('id', userId);
    body.append('old', oldPassword);
    body.append('new', newPassword);

    try {
      const {
        data: { status, error },
      } = await axios.post(saveSettingsUrl, body);
      return { success: status === 1, error };
    } catch (e) {
      if (bugsnagClient) {
        bugsnagClient.notify(e);
      }
    }
  }

  static async updateUserV2(userPayload: UpdateUserArgsV2, userId: string): Promise<boolean> {
    const {
      data: { updateUserV2 },
    } = await apolloClientInternal.mutate({
      mutation: UserMutations.updateUserV2,
      variables: { userPayload, userId },
    });

    return updateUserV2;
  }

  static async updateUserProfileV2(userProfilePayload: UpdateUserProfileInput): Promise<boolean> {
    const {
      data: { updateUserV2 },
    } = await apolloClientInternal.mutate({
      mutation: UserMutations.updateUserProfile,
      variables: { ...userProfilePayload },
    });

    return updateUserV2;
  }

  static async fetchExternalIdentityProviders(): Promise<ExternalIdentityProvider[]> {
    const { data } = await axios.get('/get/settings/identifiers');
    return (data?.providers || []).map(({ name }) => name);
  }

  static async disconnectExternalIdentityProvider(
    entityId: string,
    provider: ExternalIdentityProvider
  ): Promise<boolean> {
    const body = new FormData();
    body.append('action', 'remove-identifier');
    body.append('provider', provider);
    body.append('id', entityId);

    try {
      const {
        data: { status, error },
      } = await axios.post(saveSettingsUrl, body);
      if (error) {
        throw new Error(error);
      }
      return status === 1;
    } catch (e) {
      if (bugsnagClient) {
        bugsnagClient.notify(e);
      }
      throw e;
    }
  }

  static async uploadUserThumb(file: File): Promise<string> {
    try {
      const body = new FormData();
      body.append('file', file);

      const { data } = await axios.post('/save/user_thumb', body);

      if (data.error) {
        throw new Error(data.error);
      }

      if (!data.success) {
        throw new Error("Couldn't upload user thumb");
      }

      return data.img;
    } catch (e) {
      if (bugsnagClient) {
        bugsnagClient.notify(e);
      }
      throw e;
    }
  }

  static async saveUserThumb(userId: string, thumbData: string): Promise<string> {
    try {
      const body = new FormData();
      body.append('action', 'user');
      body.append('field', 'user-thumb');
      body.append('id', userId);
      body.append('value', thumbData);

      const { data } = await axios.post('/save/settings', body);

      if (data.error) {
        throw new Error(data.error);
      }

      if (!data.status) {
        throw new Error("Couldn't save user thumb");
      }

      return data.thumb_url;
    } catch (e) {
      if (bugsnagClient) {
        bugsnagClient.notify(e);
      }
      throw e;
    }
  }

  static async sendInvitationEmail(userId: string): Promise<void> {
    const { data: sendInvitationEmail } = await apolloClientInternal.mutate({
      mutation: UserMutations.sendInvitationEmail,
      variables: {
        userId,
      },
    });

    return sendInvitationEmail;
  }

  static async changeEmail(email: string, userId: string) {
    const body = new FormData();
    body.append('action', 'email');
    body.append('id', userId);
    body.append('email-new', email);
    const { data } = await axios.post('/save/settings', body);
    if (data.status !== 1) {
      throw new Error(data?.error);
    }
  }

  static async create(
    userProfilePayload: CreateUserProfileArgs,
    email: string,
    token?: string
  ): Promise<{ id: string }> {
    const {
      data: { createUser },
    } = await apolloClientInternal.mutate({
      mutation: UserMutations.createUser,
      variables: {
        userProfilePayload,
        userPayload: {
          email,
          firstName: '',
          lastName: '',
          timezone: '',
          mobile: '',
          phone: '',
          language: '',
          title: '',
          note: '',
          registrationSource: this.registrationSourceFromSettings,
        },
      },
      ...(token && { context: { headers: { authorization: token } } }),
    });
    return createUser.user;
  }
}
